/**
 * Badge Component
 *
 * Small status indicators and labels built with Community Trust design tokens.
 */

import React from 'react';
import { clsx } from 'clsx';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Visual style variant
   */
  variant?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'neutral';

  /**
   * Size of the badge
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Badge content
   */
  children: React.ReactNode;

  /**
   * Optional icon
   */
  icon?: React.ReactNode;

  /**
   * Dot indicator (shows colored dot, useful for status)
   */
  dot?: boolean;
}

/**
 * Badge component using Community Trust design system
 */
export const Badge: React.FC<BadgeProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  icon,
  dot,
  className,
  ...props
}) => {
  // Base styles
  const baseStyles = clsx(
    'inline-flex items-center gap-1',
    'font-medium rounded-full',
    'transition-colors duration-base'
  );

  // Variant styles
  const variantStyles = {
    primary: 'bg-brand-primary text-white',
    secondary: 'bg-brand-secondary text-white',
    success: 'bg-success-100 text-success-700',
    error: 'bg-error-100 text-error-700',
    warning: 'bg-warning-100 text-warning-700',
    info: 'bg-info-100 text-info-700',
    neutral: 'bg-neutral-200 text-neutral-700',
  };

  // Size styles
  const sizeStyles = {
    sm: 'px-2 py-0.5 text-xs',   // 8px padding, 2px vertical
    md: 'px-2.5 py-1 text-sm',   // 10px padding, 4px vertical
    lg: 'px-3 py-1.5 text-base', // 12px padding, 6px vertical
  };

  // Icon/Dot size
  const iconSize = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // Dot color
  const dotColor = {
    primary: 'bg-brand-primary',
    secondary: 'bg-brand-secondary',
    success: 'bg-success-500',
    error: 'bg-error-500',
    warning: 'bg-warning-500',
    info: 'bg-info-500',
    neutral: 'bg-neutral-500',
  };

  return (
    <span
      className={clsx(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {dot && (
        <span
          className={clsx(
            'rounded-full',
            dotColor[variant],
            size === 'sm' ? 'w-1.5 h-1.5' : size === 'md' ? 'w-2 h-2' : 'w-2.5 h-2.5'
          )}
        />
      )}

      {icon && (
        <span className={iconSize[size]}>
          {icon}
        </span>
      )}

      <span>{children}</span>
    </span>
  );
};

Badge.displayName = 'Badge';

export default Badge;
